import { StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { ErrorBoundary } from 'react-error-boundary';
import { I18nextProvider } from 'react-i18next';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import App from '@/App';
import Loading from '@/components/loading/Loading';
import ThemeProvider from '@/providers/ThemeProvider';
import { ErrorFallback } from './components/error-fallback';
import './globals.css';
import i18n from './i18n';
import { isChunkLoadError, reloadOnceForChunkError } from './utils/chunkReload';

const rootElement = document.getElementById('__root__');
const root = createRoot(rootElement);
const queryClient = new QueryClient();

// Stale build after a deploy: the old hashed chunks are gone, so imports fail.
// One guarded reload picks up the new index.html (see utils/chunkReload).
// Router-level failures are handled by the route errorElement; these listeners
// cover Vite preload failures and imports outside the router.
window.addEventListener('vite:preloadError', (event) => {
  if (reloadOnceForChunkError()) {
    event.preventDefault(); // reloading, do not surface the error
  }
});

window.addEventListener('error', (event) => {
  if (isChunkLoadError(event)) reloadOnceForChunkError();
});

window.addEventListener('unhandledrejection', (event) => {
  if (isChunkLoadError(event.reason) && reloadOnceForChunkError()) {
    event.preventDefault();
  }
});

root.render(
  <StrictMode>
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <Suspense fallback={<Loading show={true} />}>
        <I18nextProvider i18n={i18n}>
          <QueryClientProvider client={queryClient}>
            <ThemeProvider>
              <ReactQueryDevtools initialIsOpen={false} />
              <App />
            </ThemeProvider>
          </QueryClientProvider>
        </I18nextProvider>
      </Suspense>
    </ErrorBoundary>
  </StrictMode>
);
